import { CognitoUserPool } from 'amazon-cognito-identity-js'
import toast from 'react-hot-toast'

const API_BASE = import.meta.env.VITE_API_GATEWAY_URL || 'http://localhost:3001/api'

const userPool = new CognitoUserPool({
  UserPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID || '',
  ClientId: import.meta.env.VITE_COGNITO_CLIENT_ID || '',
})

export async function getToken(): Promise<string | null> {
  const msToken = localStorage.getItem('trc_ms_id_token')
  if (msToken) return msToken

  const cognitoUser = userPool.getCurrentUser()
  if (!cognitoUser) return null

  return new Promise((resolve) => {
    cognitoUser.getSession((err: any, session: any) => {
      if (err || !session?.isValid()) resolve(null)
      else resolve(session.getIdToken().getJwtToken())
    })
  })
}

/** Error thrown for non-2xx responses. Carries status + backend details so
 *  forms can render validation errors inline (e.g. details.validation_errors). */
export class ApiError extends Error {
  status: number
  details?: any
  body?: any
  constructor(message: string, status: number, details?: any, body?: any) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.details = details
    this.body = body
  }
}

/** Collect human-readable validation messages from an ApiError (or any error). */
export function getErrorMessages(err: any): string[] {
  const out: string[] = []
  const details = err?.details
  if (details) {
    const v = details.validation_errors || details.errors
    if (Array.isArray(v)) v.forEach((m: any) => out.push(typeof m === 'string' ? m : (m?.message || JSON.stringify(m))))
    else if (v && typeof v === 'object') Object.entries(v).forEach(([k, m]) => out.push(`${k}: ${String(m)}`))
  }
  if (!out.length && err?.message) out.push(err.message)
  return out
}

/** GET /users has returned both a bare array and {users:[...]}. Accept either. */
export function extractUsers<T = any>(d: any): T[] {
  return Array.isArray(d) ? d : (Array.isArray(d?.users) ? d.users : [])
}

async function apiRequest<T = any>(path: string, options: RequestInit = {}): Promise<{ data: T; status: number }> {
  const token = await getToken()

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  }
  if (token) headers['Authorization'] = `Bearer ${token}`

  const resp = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  })

  const raw = await resp.text()
  let data: any = {}
  if (raw) {
    try {
      data = JSON.parse(raw)
    } catch {
      data = { error: raw }
    }
  }

  if (resp.status === 401) {
    toast.error('Session expired — please sign in again')
    window.location.href = '/login'
    throw new Error('Unauthorized')
  }

  if (!resp.ok) {
    const message = data?.error || data?.message || `Request failed (${resp.status})`
    throw new ApiError(message, resp.status, data?.details, data)
  }

  return { data, status: resp.status }
}

// Convenience wrappers matching the old axios-like interface
export const apiClient = {
  get: <T = any>(path: string) => apiRequest<T>(path),
  post: <T = any>(path: string, body?: any) => apiRequest<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  put: <T = any>(path: string, body?: any) => apiRequest<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  delete: <T = any>(path: string, body?: any) => apiRequest<T>(path, {
    method: 'DELETE',
    body: body === undefined ? undefined : JSON.stringify(body),
  }),
}

// Ticket-specific API helpers (used by pages)
export const ticketsApi = {
  getTickets: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return apiClient.get(`/tickets${qs}`)
  },
  getTicket: (ticketId: string) => apiClient.get(`/tickets/${ticketId}`),
  createTicket: (data: any) => apiClient.post('/tickets', data),
  /** Facilities the caller may file tickets for: {facilities:[{facilityId,name,status}], scope}. */
  myFacilities: () => apiClient.get('/users/me/facilities'),
  updateTicket: (ticketId: string, data: any) => apiClient.put(`/tickets/${ticketId}`, data),
  // Assignment goes through PUT so null (unassign) works and the same validation applies.
  assignTicket: (ticketId: string, assignedTo: string | null) => apiClient.put(`/tickets/${ticketId}`, { assignedTo }),
  getComments: (ticketId: string) => apiClient.get(`/tickets/${ticketId}/comments`),
  /** Staff viewed the notes: flips unseen notes to seen; returns {noteCount, unseenNoteCount}. */
  markNotesSeen: (ticketId: string) => apiClient.post(`/tickets/${ticketId}/notes/seen`),
  /** Short-lived signed download link for an email-reply attachment: {url, filename}. */
  getNoteAttachmentUrl: (ticketId: string, noteId: string, index: number) =>
    apiClient.get(`/tickets/${ticketId}/notes/${encodeURIComponent(noteId)}/attachments/${index}`),
  addComment: (ticketId: string, data: any) => apiClient.post(`/tickets/${ticketId}/comments`, data),
  getHistory: (ticketId: string) => apiClient.get(`/tickets/${ticketId}/history`),
  getStats: () => apiClient.get('/tickets/stats'),
  triggerRCA: (ticketId: string, force = false) => apiClient.post(`/tickets/${ticketId}/rca/trigger`, force ? { force: true } : undefined),
  getRCA: (ticketId: string) => apiClient.get(`/tickets/${ticketId}/rca`),
  approveRCA: (ticketId: string) => apiClient.post(`/tickets/${ticketId}/rca/approve`),
  rejectRCA: (ticketId: string, reason?: string) => apiClient.post(`/tickets/${ticketId}/rca/reject`, { reason }),
  listHandoffs: (status: string = 'pending') => apiClient.get(`/handoffs?status=${encodeURIComponent(status)}`),
  completeHandoff: (ticketId: string, notes: string, finalStatus: string = 'resolved', commandedBy?: string) =>
    apiClient.post(`/tickets/${ticketId}/handoff/complete`, { notes, finalStatus, commandedBy }),
  getTimeEntries: (ticketId: string) => apiClient.get(`/tickets/${ticketId}/time`),
  // Open-ticket work session: heartbeat while the ticket is open, end when it closes.
  sessionHeartbeat: (ticketId: string) => apiClient.post(`/tickets/${ticketId}/time/session`),
  sessionEnd: (ticketId: string) => apiClient.post(`/tickets/${ticketId}/time/session/end`),
  addTimeEntry: (ticketId: string, data: any) => apiClient.post(`/tickets/${ticketId}/time`, data),
  startTimer: (ticketId: string) => apiClient.post(`/tickets/${ticketId}/time/start`),
  stopTimer: (ticketId: string) => apiClient.post(`/tickets/${ticketId}/time/stop`),
  getAuditTrail: (ticketId: string) => apiClient.get(`/tickets/${ticketId}/audit`),
  getExecutions: (ticketId: string) => apiClient.get(`/tickets/${ticketId}/executions`),

  // Overhaul: explicit note with audience + email choice
  addNote: (ticketId: string, data: {
    content: string
    noteVisibility: 'internal' | 'submitter_visible'
    emailSubmitterChoice: 'send' | 'do_not_send'
    containsPhi?: boolean
    clientRequestId?: string
  }) => apiClient.post(`/tickets/${ticketId}/comments`, data),

  // 2026-09-30: render the exact submitter email (PHI = vague link-only; no PHI = full summary)
  previewNoteEmail: (ticketId: string, data: { content: string; containsPhi: boolean }) =>
    apiClient.post(`/tickets/${ticketId}/note-email/preview`, data),

  // Overhaul: private RD_CB agent command
  sendAgentCommand: (ticketId: string, data: {
    instruction: string
    commandIntent?: string
    clientRequestId?: string
  }) => apiClient.post(`/tickets/${ticketId}/agent-commands`, data),

  // Overhaul: proposal actions
  getProposals: (ticketId: string) => apiClient.get(`/tickets/${ticketId}/proposals`),
  approveProposal: (ticketId: string, proposalId: string, data: {
    proposalPayloadHash: string
    clientRequestId?: string
  }) => apiClient.post(`/tickets/${ticketId}/proposals/approve`, {
    proposalId,
    payloadHash: data.proposalPayloadHash,
    clientRequestId: data.clientRequestId,
  }),
  acknowledgeNoFurtherAction: (ticketId: string, proposalId: string, data: {
    proposalPayloadHash: string
    reason: string
    clientRequestId?: string
  }) => apiClient.post(`/tickets/${ticketId}/proposals/${proposalId}/acknowledge`, data),

  // Overhaul: explicit close
  closeTicket: (ticketId: string, data: {
    customerClosureSummary?: string
    expectedVersion?: number
    clientRequestId?: string
  }) => apiClient.put(`/tickets/${ticketId}`, {
    status: 'closed',
    customerClosureSummary: data.customerClosureSummary,
    expectedVersion: data.expectedVersion,
    clientRequestId: data.clientRequestId,
  }),

  // Overhaul: soft delete / restore
  /** Worklist: bulk action over many tickets ('restore' un-deletes; staff only). */
  bulk: (data: {
    ticketIds: string[]
    action: 'assign' | 'status' | 'priority' | 'delete' | 'category' | 'restore'
    value?: string | null
    reason?: string
  }) => apiClient.post('/tickets/bulk', data),
  /** Worklist: soft delete. The backend REQUIRES a non-empty reason (400 "reason is required"). */
  deleteTicket: (ticketId: string, reason: string) =>
    apiClient.delete(`/tickets/${ticketId}`, { reason: reason.trim() }),

  softDeleteTicket: (ticketId: string, data: {
    reason: string
    expectedVersion?: number
  }) => apiClient.delete(`/tickets/${ticketId}`, { reason: data.reason, expectedVersion: data.expectedVersion }),
  restoreTicket: (ticketId: string) => apiClient.post(`/tickets/${ticketId}/restore`),
}

export const dashboardApi = {
  getDashboard: () => apiClient.get('/dashboard'),
  getMyTickets: () => apiClient.get('/dashboard/my-tickets'),
  getSLAReport: () => apiClient.get('/dashboard/sla-report'),
  getWorkload: () => apiClient.get('/dashboard/workload'),
}

export const usersApi = {
  getUsers: () => apiClient.get('/users'),
  /** Active staff only; use for assignee pickers (not the Users admin page). */
  getAssignableUsers: () => apiClient.get('/users?assignable=1'),
  getUser: (userId: string) => apiClient.get(`/users/${encodeURIComponent(userId)}`),
  createUser: (data: { email: string; name: string; role: string }) => apiClient.post('/users', data),
  updateUser: (userId: string, data: any) => apiClient.put(`/users/${encodeURIComponent(userId)}`, data),
  deleteUser: (userId: string) => apiClient.delete(`/users/${encodeURIComponent(userId)}`),
  getProfile: () => apiClient.get('/users/me'),
  /** PUT /users/me: caller edits own name / phone (E.164) / smsOptIn / smsOnAssign. 400 on bad phone or opt-in without phone. */
  updateMe: (data: { name?: string; phone?: string | null; smsOptIn?: boolean; smsOnAssign?: boolean }) => apiClient.put('/users/me', data),
  /** Microsoft directory lookup for the Add-user flow. */
  searchDirectory: (q: string) => apiClient.get(`/users/directory?q=${encodeURIComponent(q)}`),
}

/** Per-user saved worklists (server-side, with localStorage fallback in the UI). */
export const worklistsApi = {
  get: () => apiClient.get('/users/me/worklists'),
  put: (worklists: any[]) => apiClient.put('/users/me/worklists', { worklists }),
}

// ── Routing (2026-09-30): auto-assign rules, distribution lists, broadcasts. Admin only. ──

export type RuleActionType = 'user' | 'round_robin' | 'least_loaded'
export interface RuleAction { type: RuleActionType; user?: string; pool?: string[] }
export interface TimeWindow { days?: number[]; startHour?: number; endHour?: number }
export interface RuleConditions {
  facilityIds?: string[]; categories?: string[]; ticketTypes?: string[]; priorities?: string[]
  sources?: string[]; modalities?: string[]; keywords?: string[]; submitters?: string[]
  timeWindow?: TimeWindow
}
export interface AutoAssignRule {
  ruleId: string; name: string; enabled: boolean; order: number
  conditions: RuleConditions; action: RuleAction
  createdAt?: string; updatedAt?: string; updatedBy?: string
}
export interface RuleInput { name: string; enabled?: boolean; conditions: RuleConditions; action: RuleAction }
export interface RuleTestTicket {
  title?: string; description?: string; priority?: string; category?: string
  facilityId?: string; ticketType?: string; source?: string; createdBy?: string
}
export interface RuleTestResult {
  ruleId: string | null; ruleName: string | null; assignee: string | null
  trace: { ruleId: string; matched: boolean; failedCondition: string | null }[]
}
export interface DistributionList {
  listId: string; name: string; description: string
  members: { email: string }[]; roles: string[]
  createdAt?: string; updatedAt?: string; updatedBy?: string
}
export interface DistributionListInput { name: string; description?: string; members: string[]; roles: string[] }
export interface ListPreview {
  listId: string; count: number
  recipients: { name: string; email: string; phone: string | null; willGetSms: boolean }[]
}
export type BroadcastChannel = 'email' | 'sms'
export interface BroadcastInput { listIds?: string[]; emails?: string[]; subject: string; message: string; channels: BroadcastChannel[] }
export interface Broadcast {
  broadcastId: string; subject: string; message?: string; channels: BroadcastChannel[]
  listIds: string[]; emails: string[]; status: string; recipientCount: number
  counts: Record<string, { sent: number; skipped: number; failed: number }>
  createdAt: string; createdBy: string; completedAt?: string
}

/** All return the standard {success, data} envelope; read `res.data.data`. */
export const routingApi = {
  listRules: () => apiClient.get<{ data: AutoAssignRule[] }>('/rules/auto-assign'),
  createRule: (data: RuleInput) => apiClient.post<{ data: AutoAssignRule }>('/rules/auto-assign', data),
  updateRule: (ruleId: string, data: Partial<RuleInput>) =>
    apiClient.put<{ data: AutoAssignRule }>(`/rules/auto-assign/${encodeURIComponent(ruleId)}`, data),
  deleteRule: (ruleId: string) => apiClient.delete(`/rules/auto-assign/${encodeURIComponent(ruleId)}`),
  reorderRules: (ids: string[]) => apiClient.post<{ data: AutoAssignRule[] }>('/rules/auto-assign/reorder', { ids }),
  testRules: (ticket: RuleTestTicket) => apiClient.post<{ data: RuleTestResult }>('/rules/auto-assign/test', { ticket }),

  listLists: () => apiClient.get<{ data: DistributionList[] }>('/distribution-lists'),
  createList: (data: DistributionListInput) => apiClient.post<{ data: DistributionList }>('/distribution-lists', data),
  updateList: (listId: string, data: Partial<DistributionListInput>) =>
    apiClient.put<{ data: DistributionList }>(`/distribution-lists/${encodeURIComponent(listId)}`, data),
  deleteList: (listId: string) => apiClient.delete(`/distribution-lists/${encodeURIComponent(listId)}`),
  previewList: (listId: string) =>
    apiClient.get<{ data: ListPreview }>(`/distribution-lists/${encodeURIComponent(listId)}/preview`),

  sendBroadcast: (data: BroadcastInput) => apiClient.post<{ data: Broadcast }>('/broadcasts', data),
  listBroadcasts: () => apiClient.get<{ data: Broadcast[] }>('/broadcasts'),
  getBroadcast: (id: string) => apiClient.get<{ data: Broadcast }>(`/broadcasts/${encodeURIComponent(id)}`),
}

export const categoriesApi = {
  getCategories: () => apiClient.get('/categories'),
  createCategory: (data: any) => apiClient.post('/categories', data),
  updateCategory: (catId: string, data: any) => apiClient.put(`/categories/${catId}`, data),
  deleteCategory: (catId: string) => apiClient.delete(`/categories/${catId}`),
}

/** End a work session while the page is unloading (tab/window closed).
 *  keepalive lets the request outlive the page; the token must already be in hand. */
export function endSessionOnUnload(ticketId: string, token: string | null): void {
  try {
    fetch(`${API_BASE}/tickets/${ticketId}/time/session/end`, {
      method: 'POST', keepalive: true,
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: '{}',
    }).catch(() => { /* server closes it at the last heartbeat anyway */ })
  } catch { /* ignore */ }
}
